import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { 
  Car, 
  TrendingDown, 
  DollarSign, 
  Truck, 
  ShoppingBag, 
  Percent, 
  AlertCircle, 
  CreditCard, 
  CalendarClock, 
  PlusSquare, 
  Wrench 
} from 'lucide-react';
import { 
  FourWDLoanInputs, 
  FourWDLoanResult, 
  MaintenanceEstimate,
  calculate4WDLoan,
  formatCurrency,
  calculateComparisonRate,
  getVehicleSizeByPrice
} from '../utils/fourWDLoanCalculator';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const FourWDLoanCalculator: React.FC = () => {
  // State for form inputs
  const [vehiclePrice, setVehiclePrice] = useState('60000');
  const [downPayment, setDownPayment] = useState('10000');
  const [offRoadPackage, setOffRoadPackage] = useState('5000');
  const [accessories, setAccessories] = useState('3000');
  const [loanTerm, setLoanTerm] = useState('60');
  const [interestRate, setInterestRate] = useState('6.5');
  const [balloonPayment, setBalloonPayment] = useState('0');
  const [applicationFee, setApplicationFee] = useState('395');
  const [annualFee, setAnnualFee] = useState('120');
  const [vehicleType, setVehicleType] = useState('mediumSUV');
  const [kmPerYear, setKmPerYear] = useState('15000');
  
  // State for results
  const [result, setResult] = useState<FourWDLoanResult | null>(null);
  
  // Effect to update vehicle type based on price
  useEffect(() => {
    const price = parseFloat(vehiclePrice) || 0;
    setVehicleType(getVehicleSizeByPrice(price));
  }, [vehiclePrice]);
  
  // Calculate loan
  const calculateLoan = () => {
    // Validate inputs
    const inputs: FourWDLoanInputs = {
      vehiclePrice: parseFloat(vehiclePrice) || 0,
      downPayment: parseFloat(downPayment) || 0,
      offRoadPackage: parseFloat(offRoadPackage) || 0,
      accessories: parseFloat(accessories) || 0,
      loanTerm: parseInt(loanTerm) || 60,
      interestRate: parseFloat(interestRate) || 0,
      balloonPayment: parseFloat(balloonPayment) || 0,
      applicationFee: parseFloat(applicationFee) || 0,
      annualFee: parseFloat(annualFee) || 0
    };
    
    // Calculate loan results
    const loanResult = calculate4WDLoan(inputs, vehicleType as any);
    setResult(loanResult);
  };
  
  // Get comparison rate
  const getComparisonRate = (): string => {
    if (!result) return '0.00';
    
    const rate = calculateComparisonRate(
      result.loanAmount,
      parseFloat(interestRate) || 0,
      parseInt(loanTerm) || 60,
      parseFloat(applicationFee) || 0,
      parseFloat(annualFee) || 0
    );
    
    return rate.toFixed(2);
  };
  
  // Generate chart data for payment breakdown
  const getPaymentBreakdownData = () => {
    if (!result) return [];
    
    return [
      { name: 'Principal', value: result.loanAmount },
      { name: 'Interest', value: result.totalInterest },
      { name: 'Fees', value: result.totalFees }
    ];
  };
  
  // Generate chart data for maintenance costs
  const getMaintenanceData = () => {
    if (!result) return [];
    
    const { maintenanceEstimate } = result;
    
    return [
      { name: 'Regular Maintenance', value: maintenanceEstimate.yearlyMaintenance * 5 },
      { name: 'Off-road Service', value: maintenanceEstimate.offRoadServiceCost * 5 },
      { name: 'Tires', value: maintenanceEstimate.tiresReplacement * 1.5 },
      { name: 'Suspension', value: maintenanceEstimate.suspensionService * 2 },
      { name: 'Recovery Gear', value: maintenanceEstimate.recoveryGear }
    ];
  };
  
  // Get chart data for amortization
  const getAmortizationChartData = () => {
    if (!result) return [];
    
    // Simplify the data for charting by taking every 6 months
    return result.amortizationSchedule
      .filter((_, index) => index % 6 === 0 || index === result.amortizationSchedule.length - 1)
      .map(row => ({
        name: `Month ${row.month}`,
        principal: row.principal,
        interest: row.interest,
        balance: row.balance
      }));
  };
  
  return (
    <>
      <SEOHead
        title="4WD Loan Calculator | Finance Your Off-Road Vehicle in Australia"
        description="Calculate your 4WD loan repayments including off-road packages, accessories, and maintenance costs. Specialized for Australian 4WD and off-road enthusiasts."
        keywords="4wd loan calculator, off road vehicle finance, 4x4 loan, suv finance australia, off road accessories financing, 4wd maintenance costs, 4wd total ownership cost"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "4WD Loan Calculator",
              "description": "Calculate your 4WD loan repayments including off-road packages, accessories, and maintenance costs specific to Australian off-road vehicles.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              }
            }
          `}
        </script>
      </Helmet>
      
      <Calculator
        title="4WD & Off-Road Vehicle Loan Calculator"
        description="Calculate repayments on your 4WD or off-road vehicle, including optional accessories, off-road packages and long-term maintenance costs."
        icon={Truck}
        result={result ? `Monthly Repayment: ${formatCurrency(result.monthlyPayment)}` : undefined}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Car className="h-5 w-5 text-blue-600 mr-2" />
              Vehicle Details
            </h3>
            
            <Input
              label="Vehicle Price"
              value={vehiclePrice}
              onChange={setVehiclePrice}
              unit="$"
              placeholder="e.g. 60000"
            />
            
            <Input
              label="Down Payment"
              value={downPayment}
              onChange={setDownPayment}
              unit="$"
              placeholder="e.g. 10000"
            />
            
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                <p className="text-sm text-blue-700">
                  The vehicle type (small SUV, medium SUV, large SUV, or Ute) is automatically selected based on price for maintenance estimations.
                </p>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <PlusSquare className="h-5 w-5 text-blue-600 mr-2" />
              4WD Additions
            </h3>
            
            <Input
              label="Off-Road Package"
              value={offRoadPackage}
              onChange={setOffRoadPackage}
              unit="$"
              placeholder="e.g. 5000"
            />
            
            <Input
              label="Accessories"
              value={accessories}
              onChange={setAccessories}
              unit="$"
              placeholder="e.g. 3000"
            />
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
              Loan Details
            </h3>
            
            <Input
              label="Loan Term"
              value={loanTerm}
              onChange={setLoanTerm}
              unit="months"
              placeholder="e.g. 60"
            />
            
            <Input
              label="Interest Rate"
              value={interestRate}
              onChange={setInterestRate}
              unit="%"
              placeholder="e.g. 6.5"
            />
            
            <Input
              label="Balloon Payment"
              value={balloonPayment}
              onChange={setBalloonPayment}
              unit="$"
              placeholder="e.g. 0"
            />
            
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
              Fees
            </h3>
            
            <Input
              label="Application Fee"
              value={applicationFee}
              onChange={setApplicationFee}
              unit="$"
              placeholder="e.g. 395"
            />
            
            <Input
              label="Annual Fee"
              value={annualFee}
              onChange={setAnnualFee}
              unit="$"
              placeholder="e.g. 120"
            />
          </div>
        </div>
        
        <button
          onClick={calculateLoan}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition mt-6 font-bold"
        >
          Calculate 4WD Loan Repayments
        </button>
        
        {/* Results Section */}
        {result && (
          <div className="mt-8 space-y-8">
            {/* Loan Summary */}
            <div className="p-6 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">Loan Summary</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Monthly Repayment</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.monthlyPayment)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Total Interest</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalInterest)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Total Loan Cost</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.totalCost)}</p>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Loan Amount (including fees)</p>
                  <p className="text-xl font-bold text-blue-800">{formatCurrency(result.loanAmount)}</p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <p className="text-sm text-gray-600">Comparison Rate</p>
                  <p className="text-xl font-bold text-blue-800">{getComparisonRate()}%</p>
                </div>
              </div>
            </div>
            
            {/* Loan Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="text-lg font-semibold mb-4">Payment Breakdown</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getPaymentBreakdownData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {getPaymentBreakdownData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="text-lg font-semibold mb-4">Estimated 5-Year Maintenance</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getMaintenanceData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {getMaintenanceData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {/* Maintenance Estimates */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Wrench className="h-5 w-5 text-blue-600 mr-2" />
                4WD Maintenance Cost Estimates
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700 font-medium">Annual Service & Maintenance</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(result.maintenanceEstimate.yearlyMaintenance)}/year</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700 font-medium">Off-Road Service</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(result.maintenanceEstimate.offRoadServiceCost)}/year</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700 font-medium">Tire Replacement Set</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(result.maintenanceEstimate.tiresReplacement)}</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700 font-medium">Suspension Service</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(result.maintenanceEstimate.suspensionService)}</p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-700 font-medium">Recovery Gear</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(result.maintenanceEstimate.recoveryGear)}</p>
                </div>
                
                <div className="bg-blue-600 p-4 rounded-md">
                  <p className="text-sm text-white font-medium">Total 5-Year Cost</p>
                  <p className="text-lg font-bold text-white">{formatCurrency(result.maintenanceEstimate.totalFiveYearCost)}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                These maintenance estimates are based on typical costs for a {vehicleType} in Australia with regular off-road use. 
                Actual costs may vary based on driving habits, terrain, and vehicle condition.
              </p>
            </div>
            
            {/* Amortization Chart */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold mb-4">Loan Balance Over Time</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={getAmortizationChartData()}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Line type="monotone" dataKey="balance" stroke="#10b981" name="Remaining Balance" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* 4WD Specific Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">4WD & Off-Road Vehicle Financing Tips</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <h4 className="font-semibold text-lg mb-2">Specialized 4WD Financing</h4>
                  <p className="text-gray-700">
                    Some lenders offer specialized 4WD loans that understand the higher purchase and maintenance costs of off-road vehicles, 
                    potentially offering better rates for these specific vehicles.
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <h4 className="font-semibold text-lg mb-2">Off-Road Accessories Financing</h4>
                  <p className="text-gray-700">
                    Consider including essential off-road accessories in your loan amount from the beginning rather than financing them separately, 
                    which can save on overall interest costs.
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <h4 className="font-semibold text-lg mb-2">Maintenance Considerations</h4>
                  <p className="text-gray-700">
                    4WDs typically have higher maintenance costs than standard vehicles. Budget for more frequent services, 
                    more expensive parts, and specialized work on suspension, drivetrain, and underbody components.
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-md shadow-sm">
                  <h4 className="font-semibold text-lg mb-2">Residual Value</h4>
                  <p className="text-gray-700">
                    Quality 4WD vehicles often retain their value better than standard passenger cars. 
                    This can make balloon payment structures more advantageous for some borrowers.
                  </p>
                </div>
              </div>
              
              <div className="mt-6">
                <h4 className="font-semibold text-lg mb-2">Popular Australian 4WD Financing Methods</h4>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  <li>
                    <span className="font-medium">Secured Car Loans:</span> Using the 4WD as security, often with rates from 5.99% p.a.
                  </li>
                  <li>
                    <span className="font-medium">Novated Leases:</span> Popular for employees, can offer tax advantages for eligible 4WD vehicles.
                  </li>
                  <li>
                    <span className="font-medium">Chattel Mortgage:</span> For business use 4WDs, offering potential tax benefits for ABN holders.
                  </li>
                  <li>
                    <span className="font-medium">Balloon Payment Loans:</span> Lower regular payments with a larger payment at the end, suitable for vehicles with strong resale value.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* General Information */}
        {!result && (
          <div className="mt-8 bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">About 4WD Loan Calculations</h3>
            <p className="text-gray-700 mb-4">
              Financing a 4WD or off-road vehicle involves more considerations than a standard car loan. 
              Our 4WD Loan Calculator helps you understand the full cost of ownership, including:
            </p>
            
            <ul className="list-disc pl-5 text-gray-700 space-y-2 mb-6">
              <li>Base vehicle price plus off-road packages and accessories</li>
              <li>Loan repayments with balloon payment options</li>
              <li>Estimated maintenance costs specific to 4WD vehicles</li>
              <li>Long-term ownership considerations unique to off-road vehicles</li>
            </ul>
            
            <p className="text-gray-700">
              Enter your vehicle and loan details above to get a comprehensive breakdown of your 4WD financing 
              and ownership costs over the loan term.
            </p>
          </div>
        )}
      </Calculator>
    </>
  );
};

export default FourWDLoanCalculator; 